// src/pages/Auth/ResetPassword.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { COLORS } from '../../utils/constants';
import './ResetPassword.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.resetPassword(token, formData.newPassword, formData.confirmPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/', {
          state: {
            message: 'Password reset successfully! Please login with your new password.',
            passwordReset: true,
          },
        });
      }, 3000);
    } catch (err) {
      setError(err.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[a-zA-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(pwd)) score++;
    if (score <= 1) return { label: 'Weak', color: '#dc3545', width: '25%' };
    if (score === 2) return { label: 'Fair', color: '#fd7e14', width: '50%' };
    if (score === 3) return { label: 'Good', color: '#ffc107', width: '75%' };
    return { label: 'Strong', color: '#15A759', width: '100%' };
  };

  const strength = getStrength(formData.newPassword);

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-logo">
          {success ? '✓' : '🔒'}
        </div>

        <h2>{success ? 'Password Reset!' : 'Set New Password'}</h2>

        {success ? (
          <div className="reset-success">
            <p className="reset-success-message">
              Your password has been reset successfully.
            </p>
            <p className="redirect-note">Redirecting you to login...</p>
            <button
              className="reset-btn"
              onClick={() => navigate('/')}
              style={{ backgroundColor: COLORS.PRIMARY }}
            >
              Go to Login Now
            </button>
          </div>
        ) : (
          <>
            <p className="reset-subtitle">
              Choose a strong new password for your account.
            </p>

            {error && (
              <div className="reset-error">
                {error}
                {(error.includes('expired') || error.includes('invalid') || error.includes('already been used')) && (
                  <div className="reset-error-action">
                    <button
                      onClick={() => navigate('/forgot-password')}
                      style={{ color: COLORS.PRIMARY }}
                      className="error-link-btn"
                    >
                      Request a new reset link →
                    </button>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="reset-password-form">
              {/* New Password */}
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                {/* Strength bar */}
                {strength && (
                  <div className="strength-bar-wrapper">
                    <div className="strength-bar-track">
                      <div
                        className="strength-bar-fill"
                        style={{ width: strength.width, backgroundColor: strength.color }}
                      />
                    </div>
                    <span className="strength-label" style={{ color: strength.color }}>
                      {strength.label}
                    </span>
                  </div>
                )}

                <ul className="password-rules">
                  <li className={formData.newPassword.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                  <li className={/[a-zA-Z]/.test(formData.newPassword) ? 'met' : ''}>At least one letter</li>
                  <li className={/\d/.test(formData.newPassword) ? 'met' : ''}>At least one number</li>
                  <li className={/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(formData.newPassword) ? 'met' : ''}>
                    At least one special character
                  </li>
                </ul>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirm(!showConfirm)}
                    disabled={loading}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                {/* Match indicator */}
                {formData.confirmPassword && (
                  <p
                    className="match-indicator"
                    style={{
                      color: formData.newPassword === formData.confirmPassword ? '#15A759' : '#dc3545',
                    }}
                  >
                    {formData.newPassword === formData.confirmPassword
                      ? '✓ Passwords match'
                      : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="reset-btn"
                disabled={loading}
                style={{ backgroundColor: loading ? '#ccc' : COLORS.PRIMARY }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        {!success && (
          <div className="reset-password-footer">
            <button
              className="back-to-login"
              onClick={() => navigate('/')}
              style={{ color: COLORS.PRIMARY }}
            >
              ← Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;